import type { TrackPoint } from './models';

/** Default minutes a member must stay in one place before a map pause marker. */
export const DEFAULT_STATIONARY_MARK_MINUTES = 5;

/** Slider max in company settings (2 hours). `0` = markers off. */
export const MAX_STATIONARY_MARK_MINUTES = 2 * 60;

/** GPS cluster radius for treating points as the same place. */
export const STATIONARY_DWELL_RADIUS_METERS = 50;

export function clampStationaryMarkMinutes(minutes?: number | null): number {
  if (minutes == null || Number.isNaN(Number(minutes))) {
    return DEFAULT_STATIONARY_MARK_MINUTES;
  }
  const n = Math.round(Number(minutes));
  if (n <= 0) return 0;
  return Math.min(MAX_STATIONARY_MARK_MINUTES, Math.max(1, n));
}

export function formatStationaryMarkMinutes(minutes: number): string {
  const raw = Math.round(Number(minutes));
  const m =
    raw <= 0 ? 0 : Math.min(MAX_STATIONARY_MARK_MINUTES, Math.max(1, raw));
  if (m <= 0) return 'Off';
  if (m < 60) return m === 1 ? '1 minute' : `${m} minutes`;
  const hours = Math.floor(m / 60);
  const mins = m % 60;
  if (mins === 0) return hours === 1 ? '1 hour' : `${hours} hours`;
  const hourLabel = hours === 1 ? '1 hour' : `${hours} hours`;
  const minLabel = mins === 1 ? '1 minute' : `${mins} minutes`;
  return `${hourLabel} ${minLabel}`;
}

export type StationaryDwell = {
  lat: number;
  lng: number;
  startedAt: Date;
  endedAt: Date;
  durationMs: number;
  durationMinutes: number;
};

function metersBetween(
  lat1: number,
  lng1: number,
  lat2: number,
  lng2: number,
): number {
  const r = 6371000;
  const toRad = (d: number) => (d * Math.PI) / 180;
  const dLat = toRad(lat2 - lat1);
  const dLng = toRad(lng2 - lng1);
  const a1 = toRad(lat1);
  const a2 = toRad(lat2);
  const h =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(a1) * Math.cos(a2) * Math.sin(dLng / 2) ** 2;
  return 2 * r * Math.asin(Math.min(1, Math.sqrt(h)));
}

/**
 * Detects dwells from raw breadcrumbs.
 * Long gaps between nearby points (breadcrumbs pause while stopped) count as dwells.
 */
export function detectStationaryDwells(opts: {
  points: TrackPoint[];
  minMinutes: number;
  sessionEndAt?: Date | null;
  radiusMeters?: number;
}): StationaryDwell[] {
  const threshold = clampStationaryMarkMinutes(opts.minMinutes);
  const points = opts.points;
  if (threshold <= 0 || points.length < 2) return [];

  const radius = opts.radiusMeters ?? STATIONARY_DWELL_RADIUS_METERS;
  const minMs = threshold * 60 * 1000;
  const sorted = [...points].sort((a, b) => {
    if (a.seq !== b.seq) return a.seq - b.seq;
    return a.at.getTime() - b.at.getTime();
  });

  const out: StationaryDwell[] = [];
  let i = 0;
  while (i < sorted.length) {
    const anchor = sorted[i]!;
    if (!Number.isFinite(anchor.lat) || !Number.isFinite(anchor.lng)) {
      i++;
      continue;
    }

    let j = i;
    let sumLat = anchor.lat;
    let sumLng = anchor.lng;
    let count = 1;

    while (j + 1 < sorted.length) {
      const next = sorted[j + 1]!;
      if (!Number.isFinite(next.lat) || !Number.isFinite(next.lng)) break;
      if (metersBetween(anchor.lat, anchor.lng, next.lat, next.lng) > radius) {
        break;
      }
      j++;
      sumLat += next.lat;
      sumLng += next.lng;
      count++;
    }

    let endAt: Date;
    if (j + 1 < sorted.length) {
      endAt = sorted[j + 1]!.at;
    } else if (opts.sessionEndAt && opts.sessionEndAt.getTime() > anchor.at.getTime()) {
      endAt = opts.sessionEndAt;
    } else {
      endAt = sorted[j]!.at;
    }

    const durationMs = endAt.getTime() - anchor.at.getTime();
    if (durationMs >= minMs) {
      out.push({
        lat: sumLat / count,
        lng: sumLng / count,
        startedAt: anchor.at,
        endedAt: endAt,
        durationMs,
        durationMinutes: Math.max(0, Math.floor(durationMs / 60000)),
      });
    }

    i = j + 1;
  }

  return out;
}
